























































































































































































































































































































// 3rd party libraries
import moment from 'moment';
import { get } from 'vuex-pathify';
import { mapActions, mapGetters } from 'vuex';

// utilities
import mixins, { ExtractVue } from '@/utils/mixins';

// mixins
import auth from '@/mixins/auth';
import componentState, { STATE } from '@/mixins/state';
import snackbar from '@/mixins/snackbar';

// models
import {
  IQuote,
  Quote,
  QuoteType,
  IQuoteProductCategory,
  QuoteState
} from '@/models/quote';
import { Customer } from '@/models/customer';
import { CurrencyRate } from '@/models/currency';
import {
  ConfigurationItem,
  IConfigurationItem,
  IConfigurationItemData
} from '@/models/configurator/configuration';
import QuoteCustomerSpecialPricingCard from './QuoteCustomerSpecialPricingCard.vue';
import QuoteProductSubscriptionItemBody from './QuoteProductSubscriptionItemBody.vue';
import QuoteProposalSummaryDetailCard from './QuoteProposalSummaryDetailCard.vue';
import { Module, ModulePaymentTypeEnum } from '@/models/configurator/module';
import QuoteTestBanner from './QuoteTestBanner.vue';
import QuoteReviewingDiscountsCard from './QuoteReviewingDiscountsCard.vue';
import QuoteReviewingTariffSurchargeCard from './QuoteReviewingTariffSurchargeCard.vue';
import ConfirmationDialog from '../common/ConfirmationDialog.vue';
import QuoteDetailSummaryCard from './QuoteDetailSummaryCard.vue';

export default mixins(auth, componentState, snackbar).extend({
  name: 'QuoteDetail',

  components: {
    QuoteCustomerSpecialPricingCard,
    QuoteProductSubscriptionItemBody,
    QuoteProposalSummaryDetailCard,
    QuoteTestBanner,
    QuoteReviewingDiscountsCard,
    QuoteReviewingTariffSurchargeCard,
    ConfirmationDialog,
    QuoteDetailSummaryCard
  },

  props: {
    quoteId: Number,
    quoteRevision: Number
  },

  data: () => ({
    state: STATE.LOADING,
    showDetails: false,
    /** 2025-10-13 - LH - TODO: handle case were quote is undefined and support for quote type being nullable */
    quote: (null as unknown) as Quote,
    equipmentQuoteCategories: [] as IQuoteProductCategory[],
    subscriptionQuoteCategories: [] as IQuoteProductCategory[],

    quoteProductItemsMap: null as any,
    rightQuoteDrawer: true,

    loading: false,
    QuoteState,

    isDiscountFormSynced: false,
    isDiscountFormValid: false,
    approveConfirmationDialog: false,

    isSurchargeValid: false,
    surchargeApproveConfirmationDialog: false
  }),

  computed: {
    ...mapGetters({
      getQuoteByIdAndRevision: 'quote/getQuoteByIdAndRevision'
    }),

    getCustomerById: get<(id: string) => Customer>('customer/getCustomerById'),

    getCurrencyById: get<(id: number) => CurrencyRate>(
      'currency/getCurrencyById'
    ),

    getModuleById: get<(id: number) => Module>(
      'configuratorModule/getModuleById'
    ),

    airwayIcon: get('app/airwayIcon'),

    allowEwgDiscount(): boolean {
      if (this.quote.customer?.identifier) {
        return (
          this.getCustomerById(this.quote.customer.identifier)?.type === 'EWG'
        );
      }

      return false;
    },

    isQuoteVentilation(): boolean {
      return (
        this.quote.type == QuoteType.RTU_VENTILATION ||
        this.quote.type == QuoteType.AIRWAY_VENTILATION
      );
    },

    equipmentDiscountsTotal(): number {
      // TODO: plus item discount as well

      const reducer = (accumulator: number, currentQuote: number) =>
        accumulator + currentQuote;
      return (
        this.quote.categories
          .map(category => {
            const module = this.getModuleById(category.module);
            let discount = 0;
            if (!module.is_subscription) {
              discount = category.discount!;
            }
            return discount;
          })
          .reduce(reducer, 0) || 0
      );
    },

    equipmentSubTotal(): number {
      return (
        this.quote.categories.reduce((accumulator, category) => {
          let total = accumulator;
          const module = this.getModuleById(category.module);
          if (!module.is_subscription) {
            total += category.price ?? 0;
          }
          return total;
        }, 0) - this.equipmentDiscountsTotal
      );
    },

    oneTimeSubscriptionModuleQuoteCategories(): IQuoteProductCategory[] {
      return this.filterSubscriptionQuoteCategoriesByModulePaymentType(
        ModulePaymentTypeEnum.OneTime
      );
    },

    recurringSubscriptionModuleQuoteCategories(): IQuoteProductCategory[] {
      return this.filterSubscriptionQuoteCategoriesByModulePaymentType(
        ModulePaymentTypeEnum.Recurring
      );
    },

    subscriptionDiscountsTotal(): number {
      // TODO: plus item discount as well

      const reducer = (accumulator: number, currentQuote: number) =>
        accumulator + currentQuote;
      return (
        this.quote.categories
          .map(category => {
            const module = this.getModuleById(category.module);
            let discount = 0;
            if (module.is_subscription) {
              discount = category.discount!;
            }
            return discount;
          })
          .reduce(reducer, 0) || 0
      );
    },

    subscriptionSubTotal(): number {
      return (
        this.quote.categories.reduce((accumulator, category) => {
          let total = accumulator;
          const module = this.getModuleById(category.module);
          if (module.is_subscription) {
            total += category.price ?? 0;
          }
          return total;
        }, 0) - this.subscriptionDiscountsTotal
      );
    },

    oneTimeSubscriptionFeeSubTotal(): number {
      return this.oneTimeSubscriptionModuleQuoteCategories.reduce(
        (accumulator, category) => {
          let total = accumulator;

          const netPrice = category.price! - category.discount!;

          total += netPrice!;

          return total;
        },
        0
      );
    },

    oneTimeSubscriptionFirstYearDiscountsTotal(): number {
      return this.oneTimeSubscriptionModuleQuoteCategories.reduce(
        (accumulator, category) => {
          let discountTotal = accumulator;

          const netPrice = category.price! - category.discount!;

          const firstYearSubscriptionDiscount =
            (category.first_year_subscription_discount_percentage! * netPrice) /
            100;

          discountTotal += firstYearSubscriptionDiscount;

          return discountTotal;
        },
        0
      );
    },

    recurringSubscriptionFeeTotal(): number {
      return this.recurringSubscriptionModuleQuoteCategories.reduce(
        (accumulator, category) => {
          let total = accumulator;

          const netPrice = category.price! - category.discount!;

          total += netPrice!;

          return total;
        },
        0
      );
    },

    oneTimeSubscriptionFeeNetTotal(): number {
      return (
        this.oneTimeSubscriptionFeeSubTotal -
        this.oneTimeSubscriptionFirstYearDiscountsTotal
      );
    },

    equipmentNetTotal(): number {
      return (
        this.equipmentSubTotal -
        Number(this.quote.special_discount!) -
        Number(this.quote.ewg_discount!)
      );
    },

    subscriptionNetTotal(): number {
      return (
        this.subscriptionSubTotal -
        Number(this.quote.subscription_first_year_discount!)
      );
    },

    amountTotal(): number {
      return [
        this.quote.service_items,
        this.quote.freight_items,
        this.quote.extra_items
      ]
        .flat()
        .map(item => Number(item.price! * this.quote.exchange_rate!))
        .reduce(
          (accumulator, cummalative) => accumulator + cummalative,
          this.equipmentNetTotal + this.subscriptionNetTotal
        );
    },

    isLatestRevisionQuote(): boolean {
      return this.quote.latest!;
    }
  },

  mounted() {
    this.init();
  },

  watch: {
    quoteId() {
      this.init();
    }
  },

  methods: {
    async init() {
      this.setComponentState('loading');
      Promise.all([
        this.fetchModules(),
        this.fetchCurrencies(),
        this.checkQuoteRevision()
          // Then is here as opposed to the end of the Promise.all is because we want to
          // continue as soon as the quote revision fetch is complete.
          // Instead of waiting for all of the request to be complete first.
          .then(async () => {
            const quote = this.getQuoteByIdAndRevision(
              this.quoteId,
              this.quoteRevision
            );

            await this.updateQuoteData(quote);

            if (quote.customer?.identifier) {
              this.fetchCustomer(quote.customer?.identifier);
            }
          })
      ])
        .then(() => {
          this.setComponentState('loaded');
        })
        .catch(error => {
          this.setComponentState('error', error);
        });
    },

    checkQuoteRevision() {
      return this.fetchQuoteRevision({
        quoteId: this.quoteId,
        revisionId: this.quoteRevision
      });
    },

    ...mapActions({
      fetchModules: 'configuratorModule/fetchModules',
      fetchModule: 'configuratorModule/fetchModule',
      fetchQuoteRevision: 'quote/fetchQuoteRevision',
      fetchCurrencies: 'currency/fetchCurrencies',
      fetchCustomer: 'customer/fetchCustomer'
    }),

    refreshQuote() {
      this.checkQuoteRevision().then(() => this.updateQuoteOnDetailChange());
    },

    updateQuoteOnDetailChange() {
      // refresh the quote with new that is now updated in state
      const quote = this.getQuoteByIdAndRevision(
        this.quoteId,
        this.quoteRevision
      );

      if (quote) {
        this.quote = quote;

        this.setQuoteCategoryGroups(this.quote);
      } else {
        throw new Error('Quote not found in state upon detail change');
      }
    },

    /** We need to update the local copy of quote given that the state changed  */
    updateQuoteOnStateChange(data: {
      number: number;
      revision: number;
      state: number;
    }) {
      // when update from review discounts state
      this.closeApprovalConfirmationDialog();
      const quote = this.getQuoteByIdAndRevision(data.number, data.revision);

      if (quote) {
        this.quote = quote;
      } else {
        throw new Error('Quote not found in state upon state change');
      }
    },

    async updateQuoteData(quote: Quote) {
      this.quote = quote;
      await this.setQuoteCategoryGroups(quote);
    },

    async setQuoteCategoryGroups(quote: IQuote) {
      this.equipmentQuoteCategories = [];
      this.subscriptionQuoteCategories = [];
      for (let i = 0; i < quote.categories.length; i++) {
        const item = quote.categories[i];
        const module = await this.tryGetOrFetchModule(item.module);
        if (module.is_subscription) {
          this.subscriptionQuoteCategories.push(item);
        } else {
          this.equipmentQuoteCategories.push(item);
        }
      }
    },

    async tryGetOrFetchModule(id: number) {
      let module = this.getModuleById(id);
      if (!module) {
        module = await this.fetchModule(id);
      }
      return module;
    },

    createPdf({ data, id }: { data: Blob; id: number }) {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      const linkName = `${id}-quote-proposal-${moment(new Date()).format(
        'YYYY-MM-DD-HH-mm-ss'
      )}.pdf`;
      link.href = url;

      link.setAttribute('download', linkName); //or any other extension
      document.body.appendChild(link);
      link.click();
    },

    momentFormat(date: Date | string | number, days = 0) {
      return moment(date ?? new Date())
        .add(days, 'd')
        .format('LL')
        .toString();
    },

    async openQuoteWizardFormDialog() {
      const { default: wizardFormDialog } = await import(
        '@/components/quotes/QuoteWizardFormDialog.vue'
      );
      if (wizardFormDialog) {
        const formDialog = this.$refs.quoteWizardFormDialog as ExtractVue<
          typeof wizardFormDialog
        >;
        formDialog.open(this.quote);
      } else {
        this.createSnack({
          color: 'warning darken-1',
          type: 'warning',
          right: true,
          useBtn: false,
          msg: 'Quote wizard module failed to load',
          btnText: 'Close',
          timeout: 6000,
          action: function() {}
        });
      }
    },

    filterSubscriptionQuoteCategoriesByModulePaymentType(
      paymentType: ModulePaymentTypeEnum
    ): IQuoteProductCategory[] {
      return this.subscriptionQuoteCategories.filter(category => {
        const module = this.getModuleById(category.module);
        return module && module.payment_type == paymentType;
      });
    },

    closeApprovalConfirmationDialog() {
      this.approveConfirmationDialog = false;
    }
  }
});
